import { DateTime } from "luxon";
import { useOptionallyControlledState } from "../../../hooks/useOptionallyControlledState";
import { RefObject } from "react";
import {
  MaskedDateRangeInputChange,
  MaskedDateRangeInputRef,
} from "./MaskedDateRangeInput";

export type DateFieldRangeStateChange = {
  startDate: DateTime | null;
  endDate: DateTime | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
};

export type DateFieldRangeStateChangeHandler = (
  change: DateFieldRangeStateChange,
) => void;

export type DateFieldRangeStateParam = {
  /**
   * The controlled value of the start date.
   */
  valueProp?: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  } | null;
  /**
   * The default value of the start date.
   */
  defaultValueProp?: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  } | null;
  /**
   * The ref to the input component.
   */
  inputRef: RefObject<MaskedDateRangeInputRef>;
  /**
   * The function to call when the state changes.
   */
  onChange: DateFieldRangeStateChangeHandler;
};

/**
 * This hook is used to keep the state of a DateFieldRange component in sync with the input and calendar.
 */
export type DateFieldRangeState = {
  /**
   * The start date.
   */
  startDate: DateTime | null;
  /**
   * The end date.
   */
  endDate: DateTime | null;
  /**
   * The function to set the start date.
   */
  setStartDate: (date: DateTime | null) => void;
  /**
   * The function to set the end date.
   */
  setEndDate: (date: DateTime | null) => void;
  /**
   * The function to handle the input change.
   */
  handleInputChange: (change: MaskedDateRangeInputChange) => void;
  /**
   * The function to handle the calendar selection.
   */
  handleCalendarSelection: ({
    startDate,
    endDate,
  }: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  }) => void;
};

/**
 * This is a hook for keeping state in sync between a date input and calendar.
 */
export function useDateFieldRangeState({
  valueProp,
  defaultValueProp,
  inputRef,
  onChange,
}: DateFieldRangeStateParam): DateFieldRangeState {
  const [startDate, setStartDate] =
    useOptionallyControlledState<DateTime | null>({
      controlledValue:
        valueProp !== undefined ? valueProp?.startDate : undefined,
      defaultValue:
        defaultValueProp !== undefined
          ? defaultValueProp?.startDate
          : undefined,
    });
  const [endDate, setEndDate] = useOptionallyControlledState<DateTime | null>({
    controlledValue: valueProp !== undefined ? valueProp?.endDate : undefined,
    defaultValue:
      defaultValueProp !== undefined ? defaultValueProp?.endDate : undefined,
  });

  const setSharedValue = (value: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  }) => {
    inputRef.current?.setDateRange(value.startDate, value.endDate);
    setStartDate(value.startDate);
    setEndDate(value.endDate);
  };

  const handleInputChange = (change: MaskedDateRangeInputChange) => {
    const range = change.isInputEmpty
      ? null
      : {
          startDate: change.startDate?.startOf("day") ?? null,
          endDate: change.endDate?.startOf("day") ?? null,
        };
    setStartDate(range?.startDate ?? null);
    setEndDate(range?.endDate ?? null);
    onChange?.({
      startDate: range?.startDate ?? null,
      endDate: range?.endDate ?? null,
      isInputValid: change.isInputValid,
      isInputEmpty: change.isInputEmpty,
    });
  };

  const handleCalendarSelection = ({
    startDate,
    endDate,
  }: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  }) => {
    setSharedValue({ startDate, endDate });
    onChange?.({
      startDate,
      endDate,
      isInputValid: true,
      isInputEmpty: false,
    });
  };

  return {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleCalendarSelection,
  };
}
